import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { Package, Truck, CheckCircle2, Plus, Loader2, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import CreatePartsOrderModal from './CreatePartsOrderModal';

interface Item {
  name: string;
  quantity: number;
  unit?: string;
  notes?: string;
}

interface PartsOrder {
  id: string;
  order_number: string;
  job_id: string;
  supplier?: string;
  items: Item[];
  status: string;
  notes?: string;
  ordered_by: string;
  ordered_at: string;
  received_at?: string;
}

export default function PartsOrdersSection({
  jobId,
  onJobRefresh,
}: {
  jobId: string;
  onJobRefresh?: () => void;
}) {
  const [orders, setOrders] = useState<PartsOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [receivingId, setReceivingId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/parts-orders', { params: { job_id: jobId } });
      setOrders(res.data || []);
    } catch (e) {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (jobId) load();
  }, [jobId]);

  const markReceived = async (id: string) => {
    setReceivingId(id);
    try {
      await api.put(`/parts-orders/${id}`, { status: 'Received' });
      toast.success('Marked as received');
      await load();
      onJobRefresh?.();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Failed to update');
    } finally {
      setReceivingId(null);
    }
  };

  return (
    <div className="card p-5 sm:p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4 text-slate-500" />
          <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider">
            Parts orders · {orders.length}
          </h3>
        </div>
        <button
          onClick={() => setModalOpen(true)}
          className="btn btn-primary text-sm"
        >
          <Plus className="h-4 w-4" /> New order
        </button>
      </div>

      {loading && orders.length === 0 ? (
        <div className="py-6 text-center">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" />
        </div>
      ) : orders.length === 0 ? (
        <p className="text-sm text-slate-500 py-4 text-center">
          No parts ordered for this job yet.
        </p>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => {
            const received = o.status === 'Received';
            return (
              <div
                key={o.id}
                className={`rounded-xl border p-4 ${
                  received
                    ? 'bg-emerald-50/40 border-emerald-200'
                    : 'bg-amber-50/30 border-amber-200'
                }`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold text-slate-900 text-sm">
                      {o.order_number}
                    </span>
                    {received ? (
                      <span className="chip bg-emerald-100 text-emerald-800 ring-1 ring-inset ring-emerald-200">
                        <CheckCircle2 className="h-3 w-3" /> Received
                      </span>
                    ) : (
                      <span className="chip bg-amber-100 text-amber-800 ring-1 ring-inset ring-amber-200">
                        <Truck className="h-3 w-3" /> Ordered
                      </span>
                    )}
                  </div>
                  {!received && (
                    <button
                      onClick={() => markReceived(o.id)}
                      disabled={receivingId === o.id}
                      className="btn btn-primary text-xs py-1.5 px-3"
                    >
                      {receivingId === o.id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <CheckCircle2 className="h-3 w-3" />
                      )}
                      Mark received
                    </button>
                  )}
                </div>

                <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-600 mb-3">
                  {o.supplier && (
                    <div>
                      <span className="font-semibold text-slate-500">Supplier:</span>{' '}
                      {o.supplier}
                    </div>
                  )}
                  <div>
                    <span className="font-semibold text-slate-500">Ordered by:</span> {o.ordered_by}
                  </div>
                  <div>
                    <Clock className="inline h-3 w-3 mr-1" />
                    <span>{format(new Date(o.ordered_at), 'd MMM, HH:mm')}</span>
                  </div>
                  {o.received_at && (
                    <div className="text-emerald-700">
                      <CheckCircle2 className="inline h-3 w-3 mr-1" />
                      Received {format(new Date(o.received_at), 'd MMM, HH:mm')}
                    </div>
                  )}
                </div>

                <div className="rounded-lg bg-white border border-slate-200 divide-y divide-slate-100">
                  {o.items.map((it, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-1.5 text-sm">
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-slate-900">{it.name}</span>
                        {it.notes && (
                          <span className="text-xs text-slate-500 ml-2">— {it.notes}</span>
                        )}
                      </div>
                      <span className="text-xs text-slate-500 font-mono shrink-0">
                        {it.quantity} {it.unit || 'each'}
                      </span>
                    </div>
                  ))}
                </div>

                {o.notes && (
                  <p className="text-xs text-slate-600 italic mt-2">“{o.notes}”</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {modalOpen && (
        <CreatePartsOrderModal
          jobId={jobId}
          onClose={() => setModalOpen(false)}
          onCreated={() => {
            load();
            onJobRefresh?.();
          }}
        />
      )}
    </div>
  );
}
